/**
 * PickNextFileModal — opened when the Assistant moves the In Progress
 * file to Paused or Awaiting Signature AND other paused files exist
 * (CLAUDE.md §7.6). The modal forces the user to pick the file that
 * will take over In Progress so the slot is never left empty while
 * paused work is waiting.
 *
 * Two sections:
 *   RESUME A PAUSED FILE — paused candidates, newest-first.
 *   START A NEW FILE    — submitted candidates, oldest-first.
 *                         Hidden when Submitted is empty.
 *
 * The outbound transition (pause / sent-for-signature) has NOT been
 * committed yet by the time this modal opens. Cancel is a true
 * revert: it closes the modal without writing anything, so In Progress
 * stays occupied with the original file.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pause, Play, FileText, Clock } from 'lucide-react';
import { formatDate, timeAgo } from '../../utils/dateHelpers';

export default function PickNextFileModal({
  open,
  pausedCandidates = [],
  submittedCandidates = [],
  outgoingFile,
  outgoingAction = 'pause', // 'pause' | 'send_for_signature'
  busy = false,
  onCancel,
  onSelect,
}) {
  // pickedKey is `${source}:${id}` so paused and submitted IDs can
  // never collide (they shouldn't anyway, but this keeps the dispatch
  // simple and explicit).
  const [pickedKey, setPickedKey] = useState(null);

  if (!open) return null;

  const handleBackdrop = () => {
    if (busy) return;
    onCancel?.();
  };

  const handleConfirm = () => {
    if (busy || !pickedKey) return;
    const [source, id] = pickedKey.split(':');
    const pool = source === 'paused' ? pausedCandidates : submittedCandidates;
    const picked = pool.find((f) => f.id === id);
    if (picked) onSelect?.(picked, source);
  };

  const outgoingLabel =
    outgoingAction === 'send_for_signature'
      ? 'sending this file for signature'
      : 'pausing this file';

  return (
    <div style={styles.overlay} onClick={handleBackdrop}>
      <motion.div
        className="card"
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <h3 style={styles.title}>Choose your next file</h3>
        <p style={styles.subtitle}>
          Work is waiting, so In Progress can't be left empty. Pick the
          file to take over
          {outgoingFile?.clientName ? ` before ${outgoingLabel}` : ''}.
        </p>

        {/* Resume a paused file */}
        {pausedCandidates.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Pause size={12} color="#E76F51" />
              <span>Resume a paused file</span>
            </div>
            <div style={styles.list}>
              {pausedCandidates.map((f) => {
                const key = `paused:${f.id}`;
                const selected = key === pickedKey;
                return (
                  <button
                    key={f.id}
                    type="button"
                    style={{
                      ...styles.row,
                      ...(selected ? styles.rowSelected : {}),
                    }}
                    onClick={() => setPickedKey(key)}
                    disabled={busy}
                  >
                    <div style={styles.rowMain}>
                      <div style={styles.rowName}>
                        <FileText size={14} color="#1B9AAA" />
                        <span>{f.clientName || 'Unnamed client'}</span>
                        {f.priority === 'Urgent' && (
                          <span style={styles.urgentTag}>URGENT</span>
                        )}
                      </div>
                      <div style={styles.rowMeta}>
                        {f.workCategory && (
                          <span style={styles.meta}>{f.workCategory}</span>
                        )}
                        <span style={styles.meta}>
                          <Pause size={11} /> Paused{' '}
                          {f.pausedAt ? timeAgo(f.pausedAt) : '—'}
                        </span>
                        {f.appointmentDate && (
                          <span style={styles.meta}>
                            <Clock size={11} /> {formatDate(f.appointmentDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={styles.radioWrap}>
                      <span
                        style={{
                          ...styles.radio,
                          ...(selected ? styles.radioSelected : {}),
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Start a new file (from Submitted) — hidden when Submitted is empty */}
        {submittedCandidates.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <FileText size={12} color="#1B9AAA" />
              <span>Start a new file</span>
            </div>
            <div style={styles.list}>
              {submittedCandidates.map((f) => {
                const key = `submitted:${f.id}`;
                const selected = key === pickedKey;
                return (
                  <button
                    key={f.id}
                    type="button"
                    style={{
                      ...styles.row,
                      ...(selected ? styles.rowSelected : {}),
                    }}
                    onClick={() => setPickedKey(key)}
                    disabled={busy}
                  >
                    <div style={styles.rowMain}>
                      <div style={styles.rowName}>
                        <FileText size={14} color="#1B9AAA" />
                        <span>{f.clientName || 'Unnamed client'}</span>
                        {f.priority === 'Urgent' && (
                          <span style={styles.urgentTag}>URGENT</span>
                        )}
                      </div>
                      <div style={styles.rowMeta}>
                        {f.workCategory && (
                          <span style={styles.meta}>{f.workCategory}</span>
                        )}
                        <span style={styles.meta}>
                          <Clock size={11} /> Submitted{' '}
                          {f.submittedAt ? timeAgo(f.submittedAt) : '—'}
                        </span>
                      </div>
                    </div>
                    <div style={styles.radioWrap}>
                      <span
                        style={{
                          ...styles.radio,
                          ...(selected ? styles.radioSelected : {}),
                        }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={busy || !pickedKey}
          >
            <Play size={14} /> Confirm
          </button>
        </div>
      </motion.div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '1rem',
  },
  modal: {
    maxWidth: '560px',
    width: '100%',
    padding: '1.5rem',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    margin: 0,
    marginBottom: '0.4rem',
  },
  subtitle: {
    color: '#6B7A8D',
    fontSize: '0.85rem',
    marginTop: 0,
    marginBottom: '1rem',
  },
  section: {
    marginBottom: '1rem',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#6B7A8D',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '0.4rem',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    padding: '0.7rem 0.85rem',
    background: '#F7F9FC',
    border: '1.5px solid #E1E8F0',
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  rowSelected: {
    background: 'rgba(27, 154, 170, 0.08)',
    borderColor: 'var(--cp-secondary)',
    boxShadow: '0 0 0 1px rgba(27, 154, 170, 0.2)',
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
  },
  rowName: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
    fontSize: '0.75rem',
    color: '#6B7A8D',
  },
  meta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  urgentTag: {
    background: 'rgba(231, 111, 81, 0.15)',
    color: 'var(--cp-warning)',
    fontSize: '0.65rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    borderRadius: '999px',
    letterSpacing: '0.05em',
  },
  radioWrap: {
    flexShrink: 0,
  },
  radio: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #C7D3E1',
    borderRadius: '50%',
    background: '#FFFFFF',
    transition: 'all 0.15s',
  },
  radioSelected: {
    background: 'var(--cp-secondary)',
    borderColor: 'var(--cp-secondary)',
    boxShadow: 'inset 0 0 0 3px #FFFFFF',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #E8EFF5',
  },
};
